import type { InviteDto } from '@reelplicate/api-client/model';
import {
  getListInvitesQueryKey,
  useCreateInvites,
  useListInvites,
  useResendInvite,
  useRevokeInvite,
} from '@reelplicate/api-client/team';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router';

import { getErrorMessage } from '@/lib/errors';

import { type Role, toApiRole } from './roles';

const listed = (emails: string[]) => emails.join(', ');

/**
 * A workspace's pending invites and what an owner does with them: send
 * (with the seat-limit 403 as a toast whose action goes to `upgradePath`),
 * resend and revoke. Each refreshes the list.
 */
export function useInvites(workspaceId: string | undefined, upgradePath: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invites = useListInvites(workspaceId ?? '', {
    query: { enabled: Boolean(workspaceId) },
  });
  const refresh = () =>
    workspaceId &&
    queryClient.invalidateQueries({ queryKey: getListInvitesQueryKey(workspaceId) });

  const create = useCreateInvites({ mutation: { meta: { silent: true } } });
  const resendInvite = useResendInvite({ mutation: { onSuccess: refresh } });
  const revokeInvite = useRevokeInvite({ mutation: { onSuccess: refresh } });

  /** Resolves true once sent; failures are toasted and resolve false. */
  async function send(emails: string[], role: Role) {
    if (!workspaceId) return false;
    try {
      const result = await create.mutateAsync({
        id: workspaceId,
        data: { emails, role: toApiRole(role) },
      });
      void refresh();
      const notes = [
        result.alreadyMembers.length > 0 &&
          `Already in the workspace: ${listed(result.alreadyMembers)}.`,
        result.notSent.length > 0 &&
          `Couldn’t email ${listed(result.notSent)} - resend from Pending invites.`,
      ].filter(Boolean);
      const description = notes.length > 0 ? notes.join(' ') : undefined;
      if (result.invites.length === 0) toast.info('No invites sent', { description });
      else if (result.notSent.length > 0)
        toast.warning('Some invites weren’t emailed', { description });
      else toast.success('Invites sent', { description });
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      // 403 "Your plan includes N seats. Upgrade to add more."
      const seats = isAxiosError(error) && error.response?.status === 403;
      toast.error(message, {
        action: seats
          ? { label: 'Upgrade', onClick: () => void navigate(upgradePath) }
          : undefined,
      });
      return false;
    }
  }

  const resend = (invite: InviteDto) =>
    workspaceId &&
    resendInvite.mutate(
      { id: workspaceId, inviteId: invite.id },
      { onSuccess: () => toast.success(`Invite resent to ${invite.email}`) },
    );

  const revoke = (invite: InviteDto) =>
    workspaceId &&
    revokeInvite.mutate(
      { id: workspaceId, inviteId: invite.id },
      { onSuccess: () => toast(`Invite for ${invite.email} revoked`) },
    );

  return { invites, send, sending: create.isPending, resend, revoke };
}
